import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../SafeIcon';
import { FiX, FiCheck, FiLink, FiSun, FiLayers, FiType, FiEdit3, FiMaximize } from 'react-icons/fi';

const ButtonPopup = ({ onClose, onUpdate, currentStyles, availableAnchors = [] }) => {
  const [styles, setStyles] = useState({
    ...currentStyles,
    borderRadius: currentStyles.borderRadius || 8,
    linkType: currentStyles.linkType || (currentStyles.link?.startsWith('#') ? 'anchor' : 'url')
  });

  const colors = ['#3eb4b4', '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#6366f1', '#18181b'];

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
      >
        <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
          <h3 className="font-bold text-gray-800">Button Settings</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto custom-scrollbar">
          {/* Button Text Section */}
          <div className="space-y-2">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center">
              <SafeIcon icon={FiEdit3} className="mr-2" /> Button Label
            </label>
            <input
              type="text"
              value={styles.text}
              onChange={(e) => setStyles({ ...styles, text: e.target.value })}
              className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-teal-400 transition-all text-sm font-bold"
              placeholder="Enter button text..."
            />
          </div>

          {/* Corner Radius Slider */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center">
                <SafeIcon icon={FiMaximize} className="mr-2" /> Corner Rounding
              </label>
              <span className="text-xs font-bold text-teal-500">{styles.borderRadius}px</span>
            </div>
            <input
              type="range"
              min="0"
              max="50"
              value={styles.borderRadius}
              onChange={(e) => setStyles({ ...styles, borderRadius: parseInt(e.target.value) })}
              className="w-full h-1.5 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-teal-500"
            />
          </div>

          {/* Link Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center">
                <SafeIcon icon={FiLink} className="mr-2" /> Button Action
              </label>
              <select
                value={styles.linkType}
                onChange={(e) => setStyles({ ...styles, linkType: e.target.value, link: '' })}
                className="text-xs font-bold text-gray-600 bg-gray-100 border-none rounded px-2 py-1 outline-none cursor-pointer"
              >
                <option value="url">URL / Page</option>
                <option value="anchor">Anchor Point</option>
              </select>
            </div>

            {styles.linkType === 'anchor' ? (
              availableAnchors.length > 0 ? (
                <select
                  value={styles.link?.replace('#', '') || ''}
                  onChange={(e) => setStyles({ ...styles, link: e.target.value ? `#${e.target.value}` : '' })}
                  className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-teal-400 transition-all text-sm cursor-pointer"
                >
                  <option value="">Select an anchor...</option>
                  {availableAnchors.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              ) : (
                <div className="w-full p-3 bg-red-50 border border-red-100 rounded-xl text-xs font-bold text-red-500 text-center uppercase tracking-wider">
                  No anchors found in sections
                </div>
              )
            ) : (
              <input
                type="text"
                value={styles.link || ''}
                onChange={(e) => setStyles({ ...styles, link: e.target.value })}
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-teal-400 transition-all text-sm"
                placeholder="https://..."
              />
            )}
          </div>

          {/* Color Palette */}
          <div className="space-y-3">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Background Color</label>
            <div className="flex flex-wrap gap-2">
              {colors.map(c => (
                <button
                  key={c}
                  onClick={() => setStyles({ ...styles, bgColor: c })}
                  className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${styles.bgColor === c ? 'border-blue-500 ring-2 ring-blue-100' : 'border-white shadow-sm'}`}
                  style={{ backgroundColor: c }}
                />
              ))}
              <input
                type="color"
                value={styles.bgColor}
                onChange={(e) => setStyles({ ...styles, bgColor: e.target.value })}
                className="w-8 h-8 rounded-full overflow-hidden cursor-pointer border-2 border-white shadow-sm"
              />
            </div>
          </div>

          {/* Effects */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center">
                <SafeIcon icon={FiLayers} className="mr-2" /> Shadow
              </label>
              <select
                value={styles.shadow}
                onChange={(e) => setStyles({ ...styles, shadow: e.target.value })}
                className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none"
              >
                <option value="none">None</option>
                <option value="sm">Soft</option>
                <option value="md">Medium</option>
                <option value="lg">Strong</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center">
                <SafeIcon icon={FiSun} className="mr-2" /> Glow Effect
              </label>
              <button
                onClick={() => setStyles({ ...styles, glow: !styles.glow })}
                className={`w-full py-2.5 rounded-lg border-2 transition-all text-sm font-bold ${styles.glow ? 'bg-teal-50 border-teal-400 text-teal-600' : 'bg-gray-50 border-gray-100 text-gray-400'}`}
              >
                {styles.glow ? 'Glow ON' : 'Glow OFF'}
              </button>
            </div>
          </div>
        </div>

        <div className="p-6 bg-gray-50 flex space-x-3">
          <button onClick={onClose} className="flex-1 py-3 font-bold text-gray-500 hover:bg-gray-200 rounded-xl transition-colors">Discard</button>
          <button
            onClick={() => { onUpdate(styles); onClose(); }}
            className="flex-1 py-3 bg-teal-500 text-white font-bold rounded-xl shadow-lg shadow-teal-100 hover:bg-teal-600 transition-all"
          >
            Apply Changes
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default ButtonPopup;